<template>
  <div class="container">
    <donut-page-title
      class="m-bx2"
      :title="`${channelDisplayName} Pulse`"
      :breadcrumbs="breadcrumbs"
    >
      <template #actions>
        <donut-selex-search
          v-model="selectedChannelOption"
          class="w-x30"
          placeholder="Search for a channel..."
          label-key="name"
          value-key="id"
          :options="channelSearchOptions"
          :update-action="navigateToChannel"
        />
      </template>
    </donut-page-title>

    <div class="f f-je m-bx4">
      <div class="button inv" @click="showSettingsModal = true">Settings</div>
    </div>

    <team-pulse-modals-channel-pulse-settings
      v-model="showSettingsModal"
      @remove-from-pulse="showRemoveConfirmModal = true"
    />
    <team-pulse-modals-remove-channel-pulse-confirm
      v-model="showRemoveConfirmModal"
      :channel-id="channel.id"
      :channel-display-name="channelDisplayName"
    />

    <div class="f f-h f-ast f-gap-16 m-bx4">
      <div class="team-pulse-card team-pulse-main p-x2">
        <div class="f f-h f-ac f-ast m-bx2">
          <div class="f f-v f-gap-8 m-rx2">
            <cdn-svg
              :path="engagementBarsPath"
              :version="0"
              class="h-x4 w-x4"
            />
            <cdn-svg
              :path="sentimentFacePath"
              :version="0"
              class="h-x4 w-x4 m-tx2"
            />
          </div>
          <div class="f-v f-grow m-rx2">
            <div class="t-lg bold">90 day {{ channel.name }} Pulse</div>
            <div class="t-ml display m-tx1">
              <div v-if="isLoadingSummary" class="f f-h f-ac">
                <donut-loading-dots />
              </div>
              <div v-else-if="channelSummary && channelSummary.summary">
                {{ channelSummary.summary }}
              </div>
              <div v-else>
                There is no engagement data available to generate a summary.
              </div>
            </div>
          </div>
        </div>
      </div>

      <team-pulse-legend />
    </div>

    <div class="m-tx4">
      <div class="t-lg bold m-bx2">Trends</div>
      <div v-if="isLoadingTrends || trendPoints.length" class="trend-grid">
        <team-pulse-trend-chart
          title="Sentiment"
          type="sentiment"
          score-key="sentimentScore"
          :points="trendPoints"
          :loading="isLoadingTrends"
        />
        <team-pulse-trend-chart
          title="Participation"
          type="engagement"
          score-key="engagementScore"
          :points="trendPoints"
          :loading="isLoadingTrends"
        />
      </div>
      <donut-empty
        v-else
        title="No trend data yet"
        description="We need more channel pulse reports to chart trends."
        image="nucleo/ghost"
      />
    </div>

    <div class="m-tx2">
      <div class="f f-h f-ac f-sb m-bx2">
        <div class="t-lg bold">Weekly Pulse</div>
        <div class="t-sm gray">New summaries appear at the start of every week</div>
      </div>

      <div v-if="paginatedWeeklyPulseReports.length" class="f-v f-gap-16">
        <team-pulse-weekly-card
          v-for="report in paginatedWeeklyPulseReports"
          :key="report.id"
          :report="report"
        />
      </div>
      <donut-empty
        v-else
        title="No weekly reports yet"
        description="We will show up to 12 recent weekly pulse summaries here."
        image="nucleo/ghost"
      />

      <div v-if="totalWeeklyPages > 1" class="f f-h f-jc f-ac m-tx2 weekly-pagination">
        <button
          class="button-reset pagination-btn f-h f-ac"
          type="button"
          :class="{ 'pagination-btn--enabled': currentWeeklyPage > 0 }"
          :disabled="currentWeeklyPage === 0"
          @click="goToPreviousWeeklyPage"
        >
          <i class="mi md-20" :class="currentWeeklyPage > 0 ? 'purple' : 'gray'">chevron_left</i>
        </button>
        <button
          class="button-reset pagination-btn f-h f-ac"
          type="button"
          :class="{ 'pagination-btn--enabled': currentWeeklyPage < totalWeeklyPages - 1 }"
          :disabled="currentWeeklyPage >= totalWeeklyPages - 1"
          @click="goToNextWeeklyPage"
        >
          <i class="mi md-20" :class="currentWeeklyPage < totalWeeklyPages - 1 ? 'purple' : 'gray'">chevron_right</i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import Network from 'lib/api/network';
import useEngagementReportIcons from 'composables/team-pulse/icons';

const props = defineProps({
  channel: {
    type: Object,
    required: true,
  },
  enabledChannels: {
    type: Array,
    default: () => [],
  },
  weeklyPulseReports: {
    type: Array,
    default: () => [],
  },
});

const channelSummary = ref(null);
const isLoadingSummary = ref(false);
const isLoadingTrends = ref(false);
const trendPoints = ref([]);
const selectedChannelOption = ref(null);
const currentWeeklyPage = ref(0);
const weeklyPageSize = 4;
const showSettingsModal = ref(false);
const showRemoveConfirmModal = ref(false);

const channelDisplayName = computed(() => {
  const { name } = props.channel;
  return name?.startsWith('#') ? name : `#${name}`;
});

const breadcrumbs = computed(() => [
  { label: 'Team Pulse', url: '/team_pulse' },
  { label: channelDisplayName.value, url: `/team_pulse/channels/${props.channel.id}` },
]);

const channelOptions = computed(() => props.enabledChannels.filter((option) => option.id !== props.channel.id));
const channelSearchOptions = computed(() => channelOptions.value.map((option) => ({
  id: option.id,
  name: `#${option.name}`,
})));
const totalWeeklyPages = computed(() => Math.ceil(props.weeklyPulseReports.length / weeklyPageSize));
const paginatedWeeklyPulseReports = computed(() => {
  const start = currentWeeklyPage.value * weeklyPageSize;
  return props.weeklyPulseReports.slice(start, start + weeklyPageSize);
});

const { engagementBarsPath, sentimentFacePath } = useEngagementReportIcons(
  computed(() => channelSummary.value || {}),
);

const fetchChannelSummary = () => {
  isLoadingSummary.value = true;
  Network.get(`/team_pulse/channels/${props.channel.id}/summary`, {
    success: (data) => {
      channelSummary.value = data;
    },
    error: () => {
      channelSummary.value = null;
    },
    complete: () => {
      isLoadingSummary.value = false;
    },
  });
};

const fetchChannelTrends = () => {
  isLoadingTrends.value = true;
  Network.get(`/team_pulse/channels/${props.channel.id}/trends`, {
    success: (data) => {
      trendPoints.value = Array.isArray(data?.points) ? data.points : [];
    },
    error: () => {
      trendPoints.value = [];
    },
    complete: () => {
      isLoadingTrends.value = false;
    },
  });
};

const navigateToChannel = (_selectedKey, option) => {
  const targetChannelId = Number(option?.id);
  if (!targetChannelId || targetChannelId === props.channel.id) return;
  window.location.assign(`/team_pulse/channels/${targetChannelId}`);
};

const goToPreviousWeeklyPage = () => {
  if (currentWeeklyPage.value <= 0) return;
  currentWeeklyPage.value -= 1;
};

const goToNextWeeklyPage = () => {
  if (currentWeeklyPage.value >= totalWeeklyPages.value - 1) return;
  currentWeeklyPage.value += 1;
};

onMounted(() => {
  fetchChannelSummary();
  fetchChannelTrends();
});
</script>

<style scoped lang="scss">
@import '../../styles/donut';

.team-pulse-card {
  border: none;
  border-radius: 12px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
}

.team-pulse-main {
  flex: 2;
  min-width: 0;
  background-color: $purple-bg-light;
}

.trend-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.weekly-pagination {
  gap: 8px;
}

.pagination-btn {
  padding: 4px;
  cursor: default;

  i {
    transition: color 0.15s;
  }
}

.pagination-btn--enabled {
  cursor: pointer;

  &:hover i {
    color: $dark-purple;
  }
}

.pagination-btn:disabled {
  cursor: default;
  pointer-events: none;
}
</style>
